import { useMemo, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";

import { GenericTooltip } from "@components/tooltip-container";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";

export type PillData<T> = {
  value: T;
  displayValue?: string;
};

export function TogglablePills<T>({
  pills,
  selectedValue,
  onClick,
  showCount,
  pillWidthClassname,
  maxWidth,
}: {
  pills: PillData<T>[];
  selectedValue: T;
  onClick: (value: T) => void;
  showCount?: number;
  pillWidthClassname?: string;
  maxWidth?: string;
}) {
  const [hoveredPill, setHoveredPill] = useState<null | number>(null);
  const pillsToShow = pills.slice(0, showCount);
  const pillsInDropdown = pills.slice(showCount);

  const onPillHover = (isHovered: boolean, pillIndex: number) => {
    if (!isHovered) {
      setHoveredPill(null);
    } else {
      setHoveredPill(pillIndex === -1 ? pillsToShow.length : pillIndex);
    }
  };

  const selectedPillIndex = useMemo(() => {
    return pills.findIndex((p) => p.value === selectedValue);
  }, [pills, selectedValue]);

  return (
    <div className="w-fit h-8.5 flex justify-center text-center cursor-pointer">
      {pillsToShow.map((pill, i) => (
        <Pill
          index={i}
          key={String(pill.value)}
          value={pill.value}
          displayValue={pill.displayValue ?? String(pill.value)}
          isSelected={selectedValue === pill.value}
          isLeftmost={i === 0}
          isRightmost={i === pills.length - 1}
          onClick={onClick}
          hideRightBorder={
            !!(
              (hoveredPill && hoveredPill - 1 === i) ||
              (selectedPillIndex && selectedPillIndex - 1 === i)
            )
          }
          widthClassname={pillWidthClassname}
          onHover={onPillHover}
          maxWidth={maxWidth}
        />
      ))}
      {pillsInDropdown.length > 0 && (
        <MorePill
          pills={pillsInDropdown}
          selectedValue={selectedValue}
          onClick={onClick}
          onHover={onPillHover}
          maxWidth={maxWidth}
        />
      )}
    </div>
  );
}

type MorePillProps<T> = {
  pills: PillData<T>[];
  selectedValue: T;
  onClick: (value: T) => void;
  onHover?: (isHovering: boolean, i: number) => void;
  maxWidth?: string;
};

function MorePill<T>({
  pills,
  selectedValue,
  onClick,
  onHover,
  maxWidth,
}: MorePillProps<T>) {
  const pillMap = useMemo(() => {
    const map = new Map<T, PillData<T>>();
    pills.forEach((pill) => map.set(pill.value, pill));
    return map;
  }, [pills]);

  const hasPillSelected = pillMap.has(selectedValue);
  const selectedPillData = hasPillSelected ? pillMap.get(selectedValue) : null;
  const pillText =
    hasPillSelected && selectedPillData
      ? selectedPillData.displayValue ?? String(selectedPillData.value)
      : "More";

  return (
    <>
      <Menu as="div" className={clsx("relative z-2", maxWidth)}>
        {({ open: isOpen }) => (
          <>
            <MenuButton
              className={clsx(
                "border border-v2-secondary h-full px-2 flex flex-row",
                "items-center gap-1 rounded-r-md border-l-transparent",
                {
                  "bg-cosmic text-white font-semibold w-fit border-cosmic":
                    hasPillSelected,
                  "hover:bg-v2-action-tertiary hover:border-cosmic+1":
                    !isOpen && !hasPillSelected,
                },
              )}
            >
              <span
                className={clsx("text-xs", maxWidth, {
                  "w-full": !maxWidth,
                  "text-cosmic": !hasPillSelected,
                  "overflow-hidden overflow-ellipsis whitespace-nowrap w-fit":
                    maxWidth,
                })}
              >
                {maxWidth && !isOpen ? (
                  <GenericTooltip
                    content={pillText}
                    noInfoCircle
                    hideArrow
                    className="w-fit"
                    mouseEnterDelay={1500}
                  >
                    {pillText}
                  </GenericTooltip>
                ) : (
                  pillText
                )}
              </span>
              <motion.i
                className={clsx("fa-regular fa-sharp fa-chevron-down text-xs", {
                  "text-cosmic": !hasPillSelected,
                })}
                initial={false}
                animate={{ rotate: isOpen ? -180 : 0 }}
                transition={{ duration: 0.2 }}
              />
            </MenuButton>
            <Transition
              enter="transition duration-100 ease-out"
              enterFrom="transform scale-95 opacity-0"
              enterTo="transform scale-100 opacity-100"
              leave="transition duration-75 ease-out"
              leaveFrom="transform scale-100 opacity-100"
              leaveTo="transform scale-95 opacity-0"
            >
              <MenuItems
                className={clsx(
                  "absolute rounded-md bg-v2-primary text-left text-sm elevation-1 w-[15rem] max-h-[25rem] overflow-auto",
                )}
              >
                {pills.map((pill, index) => {
                  const props = {
                    label: pill.displayValue ?? pill.value,
                    disabled: false,
                    onClick: () => {
                      onClick(pill.value);
                    },
                  };
                  return (
                    <MenuItem key={index}>
                      {({ active }) => {
                        return (
                          <div
                            {...props}
                            onClick={() => props.onClick()}
                            className={clsx("px-3 py-2", {
                              "bg-v2-highlight-information": active,
                            })}
                          >
                            {String(props.label)}
                          </div>
                        );
                      }}
                    </MenuItem>
                  );
                })}
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </>
  );
}

type PillProps<T> = PillData<T> & {
  index: number;
  isLeftmost: boolean;
  isRightmost: boolean;
  isSelected: boolean;
  onClick: (value: T) => void;
  widthClassname?: string;
  onHover?: (isHovering: boolean, i: number) => void;
  hideRightBorder?: boolean;
  maxWidth?: string;
};

function Pill<T>({
  index,
  value,
  displayValue,
  isLeftmost,
  isRightmost,
  isSelected,
  onClick,
  onHover,
  hideRightBorder = false,
  widthClassname = "w-24",
  maxWidth,
}: PillProps<T>) {
  return (
    <div
      className="h-8.5 text-center cursor-pointer"
      onMouseEnter={() => !isSelected && onHover?.(true, index)}
      onMouseLeave={() => onHover?.(false, index)}
    >
      <div
        className={clsx(
          "border border-v2-secondary h-full px-2 flex flex-row",
          "items-center gap-1 px-2",
          widthClassname,
          maxWidth,
          {
            "hover:bg-v2-action-tertiary hover:border-cosmic+1 hover:border":
              !isSelected,
            "text-v2-primary border-v2-secondary": !isSelected,
            "rounded-l-md": isLeftmost,
            "border-l-transparent rounded-l-0": !isLeftmost,
            "rounded-r-md": isRightmost,
            "rounded-r-0": !isRightmost,
            "border-r-transparent": hideRightBorder,
            "bg-v2-action text-white font-semibold border-v2-action":
              isSelected,
          },
        )}
        onClick={() => onClick(value)}
      >
        <span
          className={clsx("text-xs w-full", {
            "overflow-hidden whitespace-nowrap overflow-ellipsis": maxWidth,
          })}
        >
          {maxWidth ? (
            <GenericTooltip
              className="w-fit"
              hideArrow
              content={displayValue}
              noInfoCircle
              mouseEnterDelay={1500}
            >
              {displayValue}
            </GenericTooltip>
          ) : (
            displayValue
          )}
        </span>
      </div>
    </div>
  );
}
